import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * Draw-your-signature box. Pointer events, so mouse, finger and stylus all
 * work; `touch-action: none` stops the page scrolling under a finger that is
 * signing on a phone, which is where most clients sign.
 *
 * Calls onChange with a PNG data URL after each stroke, or "" when cleared.
 * The server re-encodes whatever it receives, so this does not try to be
 * clever about size — it only keeps the canvas at a sensible resolution.
 */
export default function SignaturePad({ onChange, height = 160, disabled = false, dark = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const hasInk = useRef(false);
  const [empty, setEmpty] = useState(true);

  // Size the backing store to the element × devicePixelRatio, so lines are
  // crisp on a phone instead of blurry. Ink is always dark navy on white —
  // the PNG goes into a printed PDF, whatever the page theme is.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const w = c.offsetWidth;
    c.width = w * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1e3a8a";
  }, [height]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk.current) { hasInk.current = true; setEmpty(false); }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    setEmpty(true);
    onChange?.("");
  };

  return (
    <div className="space-y-1.5">
      <div className="relative">
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          style={{ height, touchAction: "none" }}
          className={`block w-full cursor-crosshair rounded-lg border-2 border-dashed bg-white ${
            dark ? "border-slate-600" : "border-slate-300"
          } ${disabled ? "opacity-50" : ""}`}
        />
        {empty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-slate-400">
            Sign here with your mouse or finger
          </span>
        )}
        <div className="pointer-events-none absolute bottom-6 left-6 right-6 border-b border-slate-300" />
      </div>
      <button
        type="button"
        onClick={clear}
        disabled={empty || disabled}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <Eraser className="h-3.5 w-3.5" /> Clear
      </button>
    </div>
  );
}
